import React from "react";

import AlertCircleIcon from "@uui/AlertCircle";
import LockIcon from "@uui/Lock03";
import PlusIcon from "@uui/Plus";
import { isEmpty } from "lodash/fp";

import { SelectChangeEvent, Theme, Typography } from "@mui/material";

import AccessLevelIcon from "@app/common/access/ShareReport/AccessLevelIcon";
import { ELLIPSIS_SX } from "@app/common/styles";
import type {
  ApiEntityAccessView,
  EntityAccessLevel,
  SessionACLResponse,
} from "@app/common/types";
import { getInitials } from "@app/common/utils";
import { lightOrDark } from "@app/embedded/theme/utils";

import Avatar from "@rui/Avatar";
import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
} from "@rui/EmptyStateContainer";
import LoadSpinner from "@rui/LoadSpinner";
import Stack, { Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

import AccessSelector from "./AccessSelector";
import { EntityList } from "./styles";
import { getAccessLevel } from "./utils";

export interface AccessLevelChange {
  entityId: string;
  accessLevel: EntityAccessLevel;
  skipConfirmation?: boolean;
}

const EntityWithAccess = ({
  label,
  initials,
  accessLevel,
  entity,
  onChange,
  onRemove,
  useAccessLevelIcons = false,
}: {
  label: string;
  accessLevel: EntityAccessLevel;
  initials: string;
  entity: ApiEntityAccessView;
  onChange: (event: AccessLevelChange) => void;
  onRemove: (entityId: string) => void;
  useAccessLevelIcons?: boolean;
}) => {
  const handleChange = (e: SelectChangeEvent) => {
    const newAccessLevel = e.target.value as EntityAccessLevel | "remove";
    if (newAccessLevel === "remove") {
      onRemove(entity.entityId);
    } else if (newAccessLevel !== accessLevel) {
      onChange({ entityId: entity.entityId, accessLevel: newAccessLevel });
    }
  };

  return (
    <Row variant="apart" spacing={1}>
      <Row variant="left" spacing={1} sx={{ overflow: "hidden" }}>
        <Avatar name={label}>{initials}</Avatar>
        <Typography
          title={label}
          variant="bodyPrimaryReg"
          sx={{
            color: (theme) =>
              lightOrDark("shades.neutral.600", "shades.neutral.100")(theme),
            ...ELLIPSIS_SX,
          }}
        >
          {label}
        </Typography>
      </Row>
      {accessLevel === "owner" || !entity.isMutable ? (
        <Row
          variant={useAccessLevelIcons ? "apart" : "center"}
          sx={{
            pr: 0.5,
            pl: 1, // match dropdown padding-left
            backgroundColor: "background.default",
            borderRadius: "8px",
            padding: "6px 8px",
            minWidth: useAccessLevelIcons ? "58px" : "87px",
          }}
          spacing={0.5}
        >
          {useAccessLevelIcons ? (
            <AccessLevelIcon disabled accessLevel={accessLevel} />
          ) : (
            <Typography
              variant="bodySecondaryMed"
              sx={{
                color: (theme: Theme) =>
                  lightOrDark(
                    "shades.neutral.600",
                    "shades.neutral.100"
                  )(theme),
                whiteSpace: "nowrap",
              }}
            >
              {getAccessLevel(accessLevel)?.label}
            </Typography>
          )}

          <LockIcon sx={{ fontSize: "16px", color: "shades.neutral.300" }} />
        </Row>
      ) : (
        <AccessSelector
          value={accessLevel}
          onChange={handleChange}
          useIcons={useAccessLevelIcons}
        />
      )}
    </Row>
  );
};

const getAccessPropsFromEntity = (accessEntity: ApiEntityAccessView) => ({
  label: accessEntity.name,
  initials: getInitials(accessEntity.name) ?? "",
  accessLevel: accessEntity.accessLevel,
  entity: accessEntity,
});

const AccessList = ({
  entities,
  heading,
  isError = false,
  isLoading = false,
  onChange,
  onRemove,
  useAccessLevelIcons,
  maxHeight,
}: {
  entities: SessionACLResponse["entityAccess"];
  heading?: { title: string; tooltip: string };
  isError?: boolean;
  isLoading?: boolean;
  onChange: (event: AccessLevelChange) => void;
  onRemove: (entityId: string) => void;
  useAccessLevelIcons?: boolean;
  maxHeight?: React.CSSProperties["maxHeight"];
}) => {
  return (
    <EntityList spacing={1} maxHeight={maxHeight}>
      {heading && (
        <Row variant="left" spacing={0.5}>
          <Typography variant="labelMed">{heading.title}</Typography>
          <TooltipIcon title={heading.tooltip} height={12} />
        </Row>
      )}
      {isLoading ? (
        <Stack variant="center" flex={1}>
          <LoadSpinner />
        </Stack>
      ) : isEmpty(entities) ? (
        <EmptyStateContainer>
          <EmptyStateIcon
            Icon={isError ? AlertCircleIcon : PlusIcon}
            padding={2}
          />
          {isError ? (
            <EmptyStateSubtext>
              Something went wrong. Please refresh the page or contact
              support@read.ai if the problem persists.
            </EmptyStateSubtext>
          ) : (
            <EmptyStateSubtext>
              You're not sharing with anyone, yet. Add a workspace, team, or
              person above.
            </EmptyStateSubtext>
          )}
        </EmptyStateContainer>
      ) : (
        entities.map((u) => (
          <EntityWithAccess
            key={u.entityId}
            {...getAccessPropsFromEntity(u)}
            onChange={onChange}
            onRemove={onRemove}
            useAccessLevelIcons={useAccessLevelIcons}
          />
        ))
      )}
    </EntityList>
  );
};

export default AccessList;
